import { SettingsVoiceIcon } from '@/components/displays/CustomIcons';
import DeleteIcon from '@mui/icons-material/Delete';

import { capture } from '@/handlers/error';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { ResizeBorder } from '@/pages/LessonEditor/components/ResizeBorder';
import { useComponentEditor } from '@/providers/editor/EditorProvider';
import { Coordinate, isSlideComponentPronunciationAssessment, PropsWithPreview, Size, SlideComponent } from '@/types/slide';
import NotesIcon from '@mui/icons-material/Notes';
import { Box, Button, Typography } from '@mui/material';
import { useNotifications } from '@toolpad/core';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'rooks';
import { ComponentLabel } from '../ComponentLabel';
import { ContextualBar } from '../ContextualBar';
import { useRightClickMenu } from '../hooks/useRightClickMenu';
import { useSlideComponent } from '../hooks/useSlideComponent';
import { RightClickMenu } from '../RightClickMenu';
import { SlideRef } from '../Slide';
import { AudioRecorderReferenceTextPopover } from './AudioRecorderReferenceTextPopover';

interface Props {
    ref: React.RefObject<HTMLDivElement | null>;
    init: {
        model: SlideComponent;
        focus?: boolean;
    };
    slideRef: React.RefObject<SlideRef | null>;
}

export const SlideComponentPronunciationAssessment: React.FC<PropsWithPreview<Props>> = ({ ref, init, preview, slideRef }) => {
    const { t } = useTranslation();
    const notifications = useNotifications();
    const { deleteComponent, updateComponent, bringToFront, bringToBack, copyComponent, pasteComponent, undo, redo } = useComponentEditor();

    const { rightClickMenu, handleRightClickMenu, rightClickMenuClose } = useRightClickMenu();

    const { isSelected } = useSlideComponent(ref, [], !!preview);

    const [model, setModel] = useState<SlideComponent>({ ...init.model });
    const [isFocus, setIsFocus] = useState<boolean>(init.focus ?? false);

    const [tSize, setTSize] = useState<Size>(model.size);
    const [tPos, setTPos] = useState<Coordinate>(model.pos);

    const [aspectRatio] = useState(model.size.width / model.size.height);

    const [referenceTextAnchorEl, setReferenceTextAnchorEl] = useState<HTMLElement | null>(null);
    const isEditing = isFocus || isSelected;

    const onDeleteComponent = React.useCallback(
        async (id: string) => {
            try {
                await deleteComponent(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [deleteComponent, notifications],
    );

    const onUpdateComponent = React.useCallback(
        async (id: string, component: SlideComponent) => {
            try {
                await updateComponent(id, component);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [updateComponent, notifications],
    );

    const onBringToFrontHandler = React.useCallback(
        async (id: string) => {
            try {
                await bringToFront(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [bringToFront, notifications],
    );

    const onBringToBackHandler = React.useCallback(
        async (id: string) => {
            try {
                await bringToBack(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [bringToBack, notifications],
    );

    const debouncedUpdateComponent = useDebounce(onUpdateComponent, 500);

    useOutsideClick(ref, () => {
        if (preview) return;
        if (!ref) return;

        setIsFocus(false);
    });

    const onTextChange = React.useCallback(
        (v: string) => {
            if (!isSlideComponentPronunciationAssessment(model)) {
                return;
            }
            const updatedContent = { ...model.content, refText: v };
            setModel({ ...model, content: updatedContent });
            onUpdateComponent?.(model.id, { ...model, content: updatedContent });
        },
        [model, onUpdateComponent],
    );
    const debouncedTextChange = useDebounce(onTextChange, 500);

    if (!isSlideComponentPronunciationAssessment(model)) {
        return null;
    }

    return (
        <Box
            onContextMenu={isEditing ? handleRightClickMenu : undefined}
            ref={ref}
            id={`pronunciationAssessment-component-${model.id}`}
            sx={{
                position: 'absolute',
                left: tPos.x,
                top: tPos.y,
                m: 'inherit',
                p: 'inherit',
                textAlign: 'left',
                width: tSize.width,
                height: tSize.height,
            }}
            onClick={(e) => {
                if (!preview) {
                    e.stopPropagation();
                }
            }}
            onDoubleClick={(e) => {
                if (preview) return;
                e.stopPropagation();
                setReferenceTextAnchorEl(ref.current);
                setIsFocus(true);
            }}
        >
            <ResizeBorder
                minSize={{ width: 214, height: 168 }}
                maxSize={{ width: 642, height: 504 }}
                size={tSize}
                pos={tPos}
                preview={preview}
                isSelected={isEditing}
                onResize={(size, pos) => {
                    setTSize(size);
                    setTPos(pos);
                    const updateModel = { ...model, size, pos };
                    setModel({ ...updateModel });
                    debouncedUpdateComponent?.(model.id, { ...updateModel });
                }}
                aspectRatio={aspectRatio}
                slideRef={slideRef}
            >
                {!preview && (
                    <ComponentLabel
                        variant='caption'
                        sx={{
                            ...(!isEditing && {
                                opacity: 0, // Hide by default
                                transition: 'opacity 0.2s ease',
                                [`#pronunciationAssessment-component-${model.id}:hover &`]: {
                                    opacity: 1, // Show when text-component is hovered
                                },
                            }),
                        }}
                        noWrap
                    >
                        {t('slide.components.pronunciationAssessment.title')}
                    </ComponentLabel>
                )}
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <Box
                        sx={{
                            height: 96,
                            width: 96,
                            borderRadius: 48,
                            backgroundColor: '#00B912',
                            border: '4px solid white',
                            color: 'white',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <SettingsVoiceIcon
                            sx={{
                                height: 64,
                                width: 64,
                            }}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        width: '100%',
                        height: '20%',
                        position: 'absolute',
                        bottom: 0,
                    }}
                >
                    <Button
                        component='label'
                        variant='outlined'
                        fullWidth
                        startIcon={<SettingsVoiceIcon sx={{ color: (theme) => (theme.vars || theme).palette.grey[300] }} />}
                        sx={(theme) => ({
                            height: '100%',
                            '&.Mui-disabled': {
                                backgroundColor: (theme.vars || theme).palette.grey[100],
                                borderColor: (theme) => (theme.vars || theme).palette.grey[300],
                            },
                        })}
                        disabled
                    >
                        <Typography
                            variant='h5'
                            sx={{
                                color: (theme) => (theme.vars || theme).palette.grey[300],
                            }}
                            noWrap
                        >
                            {t('slide.components.pronunciationAssessment.recordAgain')}
                        </Typography>
                    </Button>
                </Box>
            </ResizeBorder>

            {!preview && isEditing && (
                <ContextualBar
                    startButtons={[
                        {
                            icon: <NotesIcon />,
                            tooltip: t('slide.components.pronunciationAssessment.tooltip.assessmentText'),
                            onClick: () => {
                                setReferenceTextAnchorEl(ref.current);
                            },
                        },
                    ]}
                    endButtons={[
                        {
                            icon: <DeleteIcon />,
                            tooltip: t('slide.components.pronunciationAssessment.tooltip.remove'),
                            onClick: () => {
                                onDeleteComponent?.(model.id);
                            },
                        },
                    ]}
                />
            )}
            {referenceTextAnchorEl && (
                <AudioRecorderReferenceTextPopover
                    title={t('slide.components.pronunciationAssessment.popover.assessmentText')}
                    anchorEl={referenceTextAnchorEl}
                    value={model.content.refText ?? ''}
                    onClose={() => {
                        setReferenceTextAnchorEl(null);
                        setIsFocus(false);
                    }}
                    onUpdate={(content) => {
                        debouncedTextChange?.(content);
                    }}
                />
            )}
            <RightClickMenu
                componentId={model.id}
                contextMenu={rightClickMenu}
                handleClose={rightClickMenuClose}
                bringToFront={onBringToFrontHandler}
                bringToBack={onBringToBackHandler}
                copyComponent={copyComponent}
                pasteComponent={pasteComponent}
                undo={undo}
                redo={redo}
            />
        </Box>
    );
};
